import { Link } from "react-router-dom";
import { Mail, MessageCircle, MapPin, Phone } from "lucide-react";
import {
  LOGO_URL,
  WHATSAPP_LINK,
  EMAIL,
  WHATSAPP_DISPLAY,
  COMPANY_NAME,
  COMPANY_LOCATION,
  LICENSE_NUMBER,
  GLOBAL_DISCLAIMER,
} from "../data/site";
import { SocialIcons } from "./SocialIcons";
import { WhatsAppIcon } from "./WhatsAppIcon";

const EXPLORE = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/projects", label: "Projects" },
  { to: "/developers", label: "Developers" },
  { to: "/services", label: "Our Services" },
];
const RESOURCES = [
  { to: "/investment-guide", label: "Investment Guide" },
  { to: "/faq", label: "FAQs" },
  { to: "/reviews", label: "Client Reviews" },
  { to: "/tilal-binghatti", label: "Tilal Binghatti" },
  { to: "/contact", label: "Contact Us" },
];
const LEGAL = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/disclaimer", label: "Investment Disclaimer" },
  { to: "/privacy#cookies", label: "Cookie Policy" },
  { to: "/contact", label: "Contact Us" },
];

export const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-[#050a18] border-t border-white/5 pt-20 pb-10 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 pb-14 border-b border-white/5">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-4 mb-6">
            <img src={LOGO_URL} alt={COMPANY_NAME} className="w-14 h-14 rounded-full ring-1 ring-[#c5a059]/40" />
            <div>
              <div className="font-serif-display text-lg">{COMPANY_NAME}</div>
              <div className="text-[10px] tracking-[0.4em] text-[#c5a059] mt-1 uppercase">{COMPANY_LOCATION}</div>
            </div>
          </div>

          <ul className="space-y-3 text-sm text-white/70 mb-6">
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 text-[#c5a059]" /> {COMPANY_LOCATION}</li>
            <li className="flex items-start gap-2"><Phone size={14} className="mt-0.5 text-[#c5a059]" /> UAE Brokerage License #{LICENSE_NUMBER}</li>
            <li>
              <a href={`mailto:${EMAIL}`} className="flex items-start gap-2 hover:text-[#c5a059]" data-testid="footer-email"><Mail size={14} className="mt-0.5 text-[#c5a059]" /> {EMAIL}</a>
            </li>
            <li>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-[#c5a059]" data-testid="footer-whatsapp"><WhatsAppIcon size={13} className="mt-1 text-[#25D366]" /> {WHATSAPP_DISPLAY}</a>
            </li>
          </ul>

          <div>
            <h4 className="text-[10px] tracking-[0.4em] text-white/40 uppercase mb-3">Follow us</h4>
            <SocialIcons size={40} />
          </div>
        </div>

        {/* Explore */}
        <div className="md:col-span-2">
          <h4 className="text-[10px] tracking-[0.4em] text-white/40 uppercase mb-5">Explore</h4>
          <ul className="space-y-3 text-sm">
            {EXPLORE.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-white/70 hover:text-[#c5a059]">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div className="md:col-span-3">
          <h4 className="text-[10px] tracking-[0.4em] text-white/40 uppercase mb-5">Resources</h4>
          <ul className="space-y-3 text-sm">
            {RESOURCES.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-white/70 hover:text-[#c5a059]">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Legal */}
        <div className="md:col-span-3">
          <h4 className="text-[10px] tracking-[0.4em] text-white/40 uppercase mb-5">Legal</h4>
          <ul className="space-y-3 text-sm">
            {LEGAL.map((l) => (
              <li key={l.to + l.label}><Link to={l.to} className="text-white/70 hover:text-[#c5a059]">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </div>

      {/* Global disclaimer strip */}
      <div className="max-w-7xl mx-auto pt-8">
        <p className="text-[11px] text-white/45 italic leading-relaxed" data-testid="footer-global-disclaimer">
          <span className="text-[#c5a059] font-semibold not-italic">Disclaimer:</span> {GLOBAL_DISCLAIMER} Universal Dream Properties LLC makes no representations of guaranteed rental income, guaranteed capital appreciation, or risk-free investment outcomes. All figures shown are estimated gross rental yields based on current market conditions.
        </p>
      </div>

      <div className="max-w-7xl mx-auto pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] tracking-[0.3em] text-white/35 uppercase">
        <p>© 2026 {COMPANY_NAME} · License #{LICENSE_NUMBER}</p>
        <p>Designed for Excellence · Dubai, UAE</p>
      </div>

      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noreferrer"
        className="pulse-cta-wa fixed bottom-6 left-6 z-40 w-16 h-16 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform text-white"
        aria-label="WhatsApp"
        data-testid="floating-whatsapp"
      >
        <WhatsAppIcon size={28} />
        <span className="absolute inset-0 rounded-full animate-ping bg-[#25D366] opacity-30" />
      </a>

      <a
        href={`mailto:${EMAIL}`}
        className="hidden md:flex fixed bottom-6 right-6 z-40 h-14 items-center gap-2 px-5 rounded-full glass-strong hover:text-[#c5a059] transition text-xs tracking-[0.2em] uppercase"
        aria-label="Email"
        data-testid="floating-email"
      >
        <Mail size={16} /> {EMAIL}
      </a>
    </footer>
  );
};
